import { useState } from "react";

// Helpers
import { getAPIFromDomain } from "../../../lib/api";
import UrlBuilder from "../../../lib/UrlBuilder";

// Interfaces
import { Fulfillment } from "@secondcloset/types";
type Carrier = Fulfillment.Carrier;

interface CarriersState {
  loading: boolean;
  data: Carrier[];
  error: string | null;
}

const useCarriers = (): {
  carriers: CarriersState;
  fetchCarriers: (organizationID: string, carrierID?: string) => Promise<CarriersState["data"]>;
  createCarrier: (body: FormData) => Promise<CarriersState["data"]>;
  updateCarrier: (carrierID: string, body: FormData) => Promise<CarriersState["data"]>;
} => {
  const [carriers, setCarriers] = useState<CarriersState>({
    loading: false,
    data: [],
    error: null,
  });

  const api = getAPIFromDomain("logistics");

  const fetchCarriers = (organizationID: string): Promise<CarriersState["data"]> => {
    const baseUrl = "/carriers";
    const query = {
      organization_id: organizationID,
      include_defaults: false,
    };
    const url = new UrlBuilder(baseUrl).applyQueryStringFromObject(query);

    setCarriers({ ...carriers, loading: true, error: null });
    return api
      .get(url)
      .then((res) => {
        setCarriers({ data: res.data.carriers, loading: false, error: null });
        return res.data.carriers;
      })
      .catch((error) => setCarriers({ ...carriers, loading: false, error }));
  };

  const createCarrier = (body: FormData): Promise<CarriersState["data"]> => {
    const url = "/carriers";

    setCarriers({ ...carriers, loading: true, error: null });
    return api
      .post(url, body)
      .then((res) => {
        setCarriers({ data: res.data.carriers, loading: false, error: null });
        return res.data.carriers;
      })
      .catch((error) => setCarriers({ ...carriers, loading: false, error }));
  };

  const updateCarrier = (carrierID: string, body: FormData): Promise<CarriersState["data"]> => {
    const url = `/carriers/${carrierID}`;

    setCarriers({ ...carriers, loading: true, error: null });
    return api
      .put(url, body)
      .then((res) => {
        setCarriers({ data: res.data.carriers, loading: false, error: null });
        return res.data.carriers;
      })
      .catch((error) => setCarriers({ ...carriers, loading: false, error }));
  };

  return { carriers, fetchCarriers, createCarrier, updateCarrier };
};

export { useCarriers };
